import { useAlert } from "@/app/contexts/AlertContext"
import { usePhotoViewer } from "@/app/contexts/PhotoViewerContext"
import { FastFillText } from "@/app/features/reports/components/fast-fill-text"
import TestFormContext, { FieldDefinition } from "@/app/features/reports/components/test-form/context"
import TestFormDebugger from "@/app/features/reports/components/test-form/debugger"
import { FormWrapperContext } from "@/app/features/reports/components/test-form/form-wrapper-context"
import { useSpaceCreation } from "@/app/features/reports/components/test-form/useSpaceCreation"
import { SelectedTestSuite } from "@/app/features/reports/types/selected-test-suite"
import { TestSuites } from "@/app/features/reports/types/test-specification"
import convertInspectionToFormValues from "@/app/features/reports/utils/convertInspectionToFormValues"
import { FileUploader } from "@/components/image-upload/FileUploader"
import { MediaInfo } from "@/components/image-upload/types"
import RaytdResultPicker from "@/components/raytd/raytd-result-picker"
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { featureFlags } from "@/config/features"
import findHookFormFieldRegistrationIssues from "@/utils/findHookFormFieldRegistrationIssues"
import { getDefaultFieldSettings } from '@/utils/getDefaultFieldSettings'
import { type TestFormValues, type TestStatus } from '@app.raytd.com/common/types/test-form'
import { getCustomFieldId, isCustomField } from "@app.raytd.com/common/utils/test-suites/getFieldRequirement"
import { Assessment, AssetEntity, ClassificationTree, TestSpecificationsEntity, deriveDefaultResolvedReferenceImages, electTestItemReferenceImage, fetchReferenceImages, type ReferenceImage, type ResolvedReferenceImage, useAddTestSpecificationFastFillMutation, useCreateClassificationItemMutation, useCreateTestItemMutation, useGetElementGroupsQuery, useUpdateReportTestItemMutation, useUpdateTestItemMutation } from '@app.raytd.com/store'
import { skipToken } from "@reduxjs/toolkit/query"
import { AnimatePresence, motion } from "framer-motion"
import { Loader2 } from 'lucide-react'
import React, { forwardRef, startTransition, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { FormProvider, useForm, UseFormReturn, useFormState, useWatch } from "react-hook-form"
import { toast } from "sonner"
import { TestItem, TestSuite } from 'store/src/lib/tests/entity'
import { v4 as uuid } from 'uuid'
import { useInspectionData } from "../hooks/useInspectionData"
import { useSpacesData } from "../hooks/useSpacesData"
import { useTestSuiteViewMemberships } from "../hooks/useTestSuiteViewMemberships"
import defaultValues from "../types/form-default-values"
import filterActiveCustomFields from '../utils/filterActiveCustomFields'
import { filterTestSuitesByView } from '../utils/filterTestSuitesByView'
import { useDynamicResolver, isSubmissionReadyForStatus } from '../utils/testFormValidator'
import { AssetDisplay } from "./asset-display"
import { CustomFieldsContainer } from "./custom-fields-container"
import { ClassificationItem, ElementSelector } from "./element-selector"
import { FormLabelWithIndicator } from './form-label-with-indicator'
import TestSpecificationSelector from "./hierarchical-test-select"
import { InspectionSelector } from "./inspection-selector"
import { ReportViewerContext } from "./report-viewer-context"
import { SpaceSelector } from "./space-selector"
import { StatusIndicator } from "./status-indicator"
import FieldContainer, { FieldContainerProps } from "./test-form/field-container"
import { MainTestFormWrapper, MainTestFormWrapperProps } from "./test-form/form-wrapper-container"
import TestFormHeader, { TestFormHeaderProps } from "./test-form/header"
import InspectionFormSkeleton from './test-loading-skeleton'
import { convertTestItemToServerPayload } from "@app.raytd.com/store"
import TestItemReferenceImages from '@/app/features/testsuites/specifications/TestItemReferenceImages'
import GuidanceView from '@/app/features/testsuites/specifications/GuidanceView'

const getTestSuiteForSpecification = (testSuites: TestSuites[], specificationId: number): SelectedTestSuite | undefined => {
  console.debug('selectedTestSpecificationId', specificationId);

  if (!testSuites) {
    return undefined;
  }

  for (const suite of testSuites) {
    const specification = suite.specifications.find(spec => spec.id === specificationId);
    if (specification) {
      return { suite: suite, specification: specification } as unknown as { suite: TestSuite, specification: TestSpecificationsEntity };
    }
  }
  return undefined;
}

interface TestEditFormProps {
  inspections: Assessment[],
  testItem: TestItem,
  assets: AssetEntity[],
  testSuites: TestSuites[],
  onSubmit: (data: TestFormValues) => void
  onDirtyChange?: (isDirty: boolean) => void
  initialData?: Partial<TestFormValues>
  defaultInspectionId?: number
  isLoading?: boolean
  saveStatus?: string
  enableRightContent?: boolean
  enableFastFillHighlighting?: boolean
  enableFastFillManagement?: boolean
  rightContent?: React.ReactNode,
  HeaderComponent?: React.ComponentType<TestFormHeaderProps>
  FieldContainerComponent?: React.ComponentType<FieldContainerProps>;
  FormWrapperComponent?: React.ComponentType<MainTestFormWrapperProps>;
  onErrors?: (errors: any) => void
  onCustomFieldOptionAdded?: (testSuiteId: number, fieldId: string, value: string) => Promise<{ success: boolean, field_id: string, data: any }>;
}

const TestEditForm = forwardRef<UseFormReturn<TestFormValues>, TestEditFormProps>(({
  inspections,
  assets,
  testSuites,
  testItem,
  onSubmit,
  onDirtyChange,
  initialData,
  saveStatus,
  enableRightContent,
  enableFastFillHighlighting = true,
  enableFastFillManagement = false,
  rightContent,
  onErrors,
  HeaderComponent = TestFormHeader,
  FieldContainerComponent = FieldContainer,
  FormWrapperComponent = MainTestFormWrapper,
  isLoading = false,
  onCustomFieldOptionAdded,
  defaultInspectionId,
}, ref) => {

  const [testState, setTestState] = useState({
    selectedTestSpecificationId: null as number | null,
    isTestSelected: false
  });

  const [isFormLoading, setFormIsLoading] = useState(false);

  const reportId = useContext(ReportViewerContext)?.reportId ?? null;

  const { showAlertDialog } = useAlert();

  const formId = useRef<string>(undefined);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const initialErrorState = useRef<any>({});
  const hasInitializedErrorState = useRef(false);

  // Reference image elections made before the test item has an id yet (unsaved,
  // e.g. offline/pre-save authoring). Buffered here and flushed once the item
  // is created, since there's nothing to persist against until then.
  const pendingReferenceImageElections = useRef<Record<number, boolean>>({});
  const handleReferenceImageElect = useCallback((image: ResolvedReferenceImage, included: boolean) => {
    pendingReferenceImageElections.current[image.id] = included;
  }, []);

  const [isFormValid, setIsFormValid] = useState(false);

  // Whether the current field values would validate if the record were
  // leaving Draft (i.e. moving to QA/Final). Unlike `isFormValid` — which
  // validates against the record's *live* status and therefore relaxes
  // suite-required fields while that status is still 'draft' — this checks
  // readiness for a non-draft status regardless of the record's current
  // status, so it's safe to gate the QA/Final controls with.
  const [isReadyForNonDraftStatus, setIsReadyForNonDraftStatus] = useState(false);

  const [isFormDirty, setIsFormDirty] = useState(false);

  const dirtyFlagRef = useRef(isFormDirty);
  const isProgrammaticResetRef = useRef(false);

  // Update the ref whenever isFormDirty changes
  useEffect(() => {
    dirtyFlagRef.current = isFormDirty;
  }, [isFormDirty]);

  const [areRequiredFieldsFilled, setAreRequiredFieldsFilled] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [isExistingTest, setIsExistingTest] = useState(false);

  const [createTest, { isLoading: isCreatingTest }] = useCreateTestItemMutation();
  const [updateTest, { isLoading: isUpdatingTest }] = useUpdateTestItemMutation();
  const [updateReportTest, { isLoading: isUpdatingReportTest }] = useUpdateReportTestItemMutation();

  const isSaving = isCreatingTest || isUpdatingTest || isUpdatingReportTest;

  // ---> Add local fast fill acceptance state early for handleSave dependency <---
  // Only manage fast fill state if enabled (for RapidQA mode)
  const [localFastFillAccepted, setLocalFastFillAccepted] = useState<boolean>(
    enableFastFillManagement
      ? (testItem as any)?.switches?.fast_fill_accepted ?? false
      : false
  );

  const resolver = useDynamicResolver(testSuites);

  const previousID = useRef<number | null>(null);

  useEffect(() => {
    if (testItem?.assessmentTestId !== previousID.current) {
      setIsFormReady(false);
      previousID.current = testItem?.assessmentTestId;
    }

    if (testItem?.assessmentTestId === null) {
      setIsFormReady(false);
    }

  }, [testItem?.assessmentTestId]);

  const methods = useForm<TestFormValues>({
    // resolver: createThrottledResolver(createDynamicResolver(testSuites)),
    resolver: resolver,
    shouldFocusError: false,
    defaultValues: {
      ...defaultValues
    },
    mode: "onChange",
  });

  const prevTestSuitesRef = useRef(testSuites);
  useEffect(() => {
    if (prevTestSuitesRef.current !== testSuites) {
      console.debug("Test suites data has changed, triggering revalidation.");
      methods.trigger();
      prevTestSuitesRef.current = testSuites;
    }
  }, [testSuites, methods]);

  const asset_id = useWatch({
    control: methods.control,
    name: 'assetId',
  });

  const { groupedInspections } = useInspectionData(inspections as any, assets);
  const { spaces: organizedSpaces, isLoading: isSpacesLoading } = useSpacesData(asset_id);

  const isLoadingTestSuiteData = false;

  const previousTestSuiteRef = useRef<number>(null);

  const testSpecificationId = useWatch({
    control: methods.control,
    name: "test_specification_id",
  });


  const selectedTestSuite = useMemo(() => {
    if (!testSuites || !testSpecificationId) return undefined;
    // The 'getTestSuiteForSpecification' function finds the suite for the given ID
    return getTestSuiteForSpecification(testSuites, testSpecificationId);
  }, [testSuites, testSpecificationId]);

  const guidanceEnabled = featureFlags.testGuidance();
  const { openPhotoViewer } = usePhotoViewer();

  const [specReferenceImages, setSpecReferenceImages] = useState<ReferenceImage[]>([]);
  const suiteId = selectedTestSuite?.suite.id;
  const specificationId = selectedTestSuite?.specification.id;

  useEffect(() => {
    if (!guidanceEnabled || !suiteId || !specificationId) {
      setSpecReferenceImages([]);
      return;
    }
    let cancelled = false;
    fetchReferenceImages(suiteId, specificationId)
      .then((res: any) => {
        if (cancelled) return;
        const body = res?.body;
        setSpecReferenceImages(Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : []);
      })
      .catch(() => {
        if (!cancelled) setSpecReferenceImages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [guidanceEnabled, suiteId, specificationId]);

  const handleOpenReferenceImage = useCallback((image: ReferenceImage) => {
    openPhotoViewer([{
      id: image.id,
      name: image.display_name || image.name || 'Reference image',
      size: 0,
      mime_type: 'image/jpeg',
      uri: image.url ?? image.preview_url ?? image.thumbnail_url ?? '',
      thumbnail_url: image.thumbnail_url ?? undefined,
      preview_url: image.preview_url ?? undefined,
    }]);
  }, [openPhotoViewer]);

  const resolvedReferenceImages = testItem?.referenceImages ?? [];
  const hasResolvedReferenceImages = resolvedReferenceImages.length > 0;

  const fallbackReferenceImages = useMemo(
    () => deriveDefaultResolvedReferenceImages(specReferenceImages),
    [specReferenceImages],
  );


  const previousTestSpecIdRef = useRef(testSpecificationId);
  const previousTestSuiteIdRef = useRef(selectedTestSuite?.suite.id);


  useEffect(() => {

    console.debug('selectedTestSuite', {
      testSpecificationId,
      selectedTestSuite,
      previousTestSuiteRef: previousTestSuiteRef.current
    });

    const newSuiteId = selectedTestSuite?.suite.id;

    // A. Reset dependant fields ONLY on an actual value change
    // Guard: Don't clear result during programmatic resets (e.g., after save) or when loading a different test
    const isNewTestSelection = testItem?.assessmentTestId && testItem?.assessmentTestId !== previousID.current;
    if (testSpecificationId !== previousTestSpecIdRef.current && previousTestSpecIdRef.current !== null && !isProgrammaticResetRef.current && !isNewTestSelection) {
      console.debug("✅ Test Specification ID changed. Resetting result.", {
        from: previousTestSpecIdRef.current,
        to: testSpecificationId,
      });
      methods.setValue("result", null);

    }

    // B. Reset custom fields ONLY if the entire suite has changed
    if (newSuiteId !== previousTestSuiteIdRef.current) {
      console.debug("✅ Test Suite changed. Resetting custom fields.", {
        from: previousTestSuiteIdRef.current,
        to: newSuiteId,
      });

      // Only reset custom fields for new test items
      if (!testItem?.assessmentTestId && selectedTestSuite) {
        const customFields = filterActiveCustomFields({}, selectedTestSuite.suite);
        methods.setValue('custom', customFields.custom);
      }
    }

    // C. Trigger validation if anything has changed
    if (
      testSpecificationId !== previousTestSpecIdRef.current ||
      newSuiteId !== previousTestSuiteIdRef.current
    ) {

      setTestState(prevState => ({
        ...prevState,
        selectedTestSpecificationId: selectedTestSuite.specification.id,
        isTestSelected: !!selectedTestSuite.specification.id
      }));


      setTimeout(() => methods.trigger(), 100);
    }

    // D. Update refs for the next render cycle
    previousTestSpecIdRef.current = testSpecificationId;
    previousTestSuiteIdRef.current = newSuiteId;

    // if (selectedTestSuite && selectedTestSuite.suite.id !== previousTestSuiteRef.current) {
    //   console.debug('Selected test suite changed', selectedTestSuite);

    //   if (!testItem?.assessmentTestId) {
    //     const customFields = filterActiveCustomFields({}, selectedTestSuite.suite);
    //     methods.setValue('custom', customFields.custom);
    //   }

    //   // if (originalTestSpecId !== value) {
    //   //         methods.setValue("result", null);
    //   //       }

    //   setTestState(prevState => ({
    //     ...prevState,
    //     selectedTestSpecificationId: selectedTestSuite.specification.id,
    //     isTestSelected: !!selectedTestSuite.specification.id
    //   }));

    //   setTimeout(() => {
    //     methods.trigger();
    //   }, 100);

    //   previousTestSuiteRef.current = selectedTestSuite.suite.id;
    // }
  }, [testSpecificationId, selectedTestSuite, methods, testItem]);


  const handleTestSelect = useCallback((value: number | null) => {
    // const originalTestSpecId = methods.formState.defaultValues?.test_specification_id;

    // const currentValue = methods.getValues('test_specification_id');

    // // If the value hasn't actually changed, do nothing.
    // // This is the guard that stops the unwanted reset.
    // if (value === currentValue) {
    //   console.debug('handleTestSelect: value has not changed', { originalTestSpecId, value, currentValue });
    //   return;
    // }

    // console.debug('handleTestSelect', { originalTestSpecId, value });
    // setTestState(prevState => ({
    //   ...prevState,
    //   selectedTestSpecificationId: value,
    //   isTestSelected: !!value
    // }));
    // if (value) {
    //   methods.setValue("test_specification_id", value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    //   //clear the result
    //   if (originalTestSpecId !== value) {
    //     methods.setValue("result", null);
    //   }
    // } else {
    //   methods.setValue("test_specification_id", undefined);
    // }
  }, [methods]);

  useEffect(() => {
    console.debug('isFormLoading', isFormLoading);
  }, [isFormLoading]);

  const assessmentId = useWatch({
    control: methods.control,
    name: "assessmentId",
  });

  const selectedAssessment = useMemo(() => {
    return inspections?.find(i => i.id === assessmentId);
  }, [inspections, assessmentId]);

  const membershipBySuiteId = useTestSuiteViewMemberships(selectedAssessment?.testSuiteId ?? []);

  const filteredTestSuites = useMemo(() => {
    //filter the visible test suites to those in the inspection.testSuiteId array
    if (!selectedAssessment) {
      return [];
    }

    const suitesInAssessment = testSuites.filter(suite => selectedAssessment.testSuiteId.includes(suite.id));
    // Further restrict each suite's specifications to the ones included in
    // that suite's selected Test Suite View (QA 307.06) — otherwise every
    // specification is offered here regardless of the chosen filter.
    return filterTestSuitesByView(suitesInAssessment, selectedAssessment.testSuiteViewId, membershipBySuiteId);
  }, [testSuites, testState.selectedTestSpecificationId, selectedAssessment, membershipBySuiteId]);

  // useEffect(() => {
  //   console.debug('form state dirty', methods.formState.dirtyFields);
  // }, [methods.formState.dirtyFields]);

  // const { data: assessmentElementGroups, isLoading: elementGroupsAreLoading } = useGetElementTreesQuery(
  //   selectedAssessment?.elementGroupId ?? skipToken
  // );

  const { data: assessmentElementTree, isLoading: elementTreeIsLoading } = useGetElementGroupsQuery(
    selectedAssessment?.elementGroupId ?? skipToken
  );

  const elementGroups = useMemo(() => {
    if (!assessmentElementTree || !selectedTestSuite || !selectedAssessment) {
      return [];
    }

    const allowedGroups = Object.values(assessmentElementTree).filter(group =>
      group.test_suite_types?.includes(selectedTestSuite.suite.type)
    );

    console.debug('Allowed groups', allowedGroups);

    return allowedGroups;

    // const treeIds = allowedGroups.map(group => group.id);
    // return treeIds;
  }, [assessmentElementTree, selectedTestSuite, selectedAssessment]);

  const [isFormReady, setIsFormReady] = useState(false);

  // const {
  //   currentErrorIndex, errorCount, handleNextError, handlePrevError, ErrorNavigation
  // } = useFormErrorNavigation({ methods, formRef, isFormReady });

  // const isComponentLoading = isLoading || isLoadingTestSuiteData || isSpacesLoading || elementTreeIsLoading;

  useEffect(() => {
    if (
      isLoadingTestSuiteData ||
      isSpacesLoading ||
      elementTreeIsLoading ||
      !selectedTestSuite ||
      isLoading
    ) {
      setIsFormReady(false);
    }
  }, [isLoadingTestSuiteData, isSpacesLoading, elementTreeIsLoading, selectedTestSuite, isLoading]);

  // Add this effect to track when the form is fully ready
  useEffect(() => {
    if (
      !isLoadingTestSuiteData &&
      // selectedTestSuite &&
      !isSpacesLoading &&
      !elementTreeIsLoading &&
      formRef.current &&
      !isLoading
    ) {
      // Give React a chance to finish rendering all fields
      startTransition(() => {
        setIsFormReady(true);
      });

    } else {
      console.debug('Form not ready', { isLoadingTestSuiteData, isSpacesLoading, elementTreeIsLoading, selectedTestSuite, isLoading });
    }
  }, [isLoadingTestSuiteData, isSpacesLoading, elementTreeIsLoading, selectedTestSuite, isLoading]);

  const resetForm = useCallback((
    initialData: Partial<TestFormValues & { formId: string; }> = {},
    options: { preserveDirtyState?: boolean } = {}
  ) => {

    hasInitializedErrorState.current = false;

    const testSuite = getTestSuiteForSpecification(testSuites, initialData?.test_specification_id);
    console.debug('Resetting form', { initialData, testSuite });

    // Set flag to prevent the result-clearing effect from firing during programmatic reset
    isProgrammaticResetRef.current = true;

    if (testSuite) {
      const custom = filterActiveCustomFields(initialData, testSuite.suite);

      console.debug('Resetting form with custom fields', { custom });

      methods.reset({
        ...defaultValues,
        ...initialData,
        ...(custom && { custom: custom.custom }),
      }, {
        keepDirty: options.preserveDirtyState ?? false
      });

      // Force-sync rating with incoming data (null clears stale state)
      methods.setValue('result', initialData?.result ?? null, { shouldDirty: false, shouldTouch: false, shouldValidate: false });

    } else {

      console.debug('Resetting form with initial data', initialData);

      methods.reset({
        ...defaultValues,
        ...initialData,

      }, {
        keepDirty: options.preserveDirtyState ?? false
      });

      // Force-sync rating with incoming data (null clears stale state)
      methods.setValue('result', initialData?.result ?? null, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    }

    setTestState({
      selectedTestSpecificationId: initialData?.test_specification_id ? Number(initialData?.test_specification_id) : null,
      isTestSelected: !!initialData?.test_specification_id
    });

    // Ensure spec-change effect does not treat a new test load as a user change
    previousTestSpecIdRef.current = initialData?.test_specification_id ?? null;
    // Also align suite ref if available from the resolved suite
    previousTestSuiteIdRef.current = testSuite?.suite?.id;

    formId.current = initialData?.formId ?? uuid();

    methods.trigger();

    // Reset the flag after a microtask to allow the effect to run normally on user-initiated changes
    setTimeout(() => {
      isProgrammaticResetRef.current = false;
    }, 0);

  }, [methods, testSuites]);

  const resetFormRef = useRef(resetForm);
  useEffect(() => {
    resetFormRef.current = resetForm;
  }, [resetForm]);

  const handleSave = useCallback(async (data: TestFormValues) => {

    toast.loading('Saving test...', {
      id: 'save-test-form'
    });

    const saveValues = {
      ...convertTestItemToServerPayload(data),
      reportId: reportId ?? undefined,
      assessmentTestId: testItem?.assessmentTestId ?? undefined,
      formId: formId.current,
      fastFillChanged: Array.from(fastFillChangedRef.current),
      // Only include fast fill data if management is enabled
      ...(enableFastFillManagement && {
        fast_fill_accepted: localFastFillAccepted
      })
    }

    try {

      let result = undefined;

      // we need to ensure that any deleted images are sent to the server with deleted:true; 
      // when saving as a final or qa the validation routine will remove any deleted images
      // so we need to ensure that any deleted images are sent to the server with deleted:true
      const allValues = methods.getValues();
      const deletedImages = allValues.images?.filter(img => img.deleted);
      if (deletedImages) {
        saveValues.images = allValues.images;
      }

      console.debug('Saving:', data.status, { formId, draftData: data, saveValues, deletedImages });

      if (isExistingTest) {

        if (reportId) {
          result = await updateReportTest(saveValues).unwrap();
        } else {
          result = await updateTest(saveValues).unwrap();
        }
        console.debug('Test updated', result);

      } else {
        result = await createTest(saveValues).unwrap();
        console.debug('Test created', result);

        const pendingElections = Object.entries(pendingReferenceImageElections.current);
        if (pendingElections.length > 0 && result?.assessmentTestId) {
          await Promise.all(
            pendingElections.map(([imageId, included]) =>
              electTestItemReferenceImage(result.assessmentTestId, Number(imageId), included)
            )
          );
          pendingReferenceImageElections.current = {};
        }
      }

      resetForm(convertInspectionToFormValues(result));
      onSubmit?.(result);
      fastFillChangedRef.current.clear();

      toast.success('Test saved', {
        id: 'save-test-form'
      });


      return true;
    } catch (error) {

      console.error('Error saving test', error);

      toast.error('Error saving test', {
        id: 'save-test-form'
      });

      showAlertDialog({
        title: 'Error saving test',
        description: 'An error occurred while saving the test. Please try again later.',
        confirmText: 'OK',
        cancelText: undefined
      });
    }
  }, [methods, testItem, isExistingTest, createTest, resetForm, onSubmit, showAlertDialog, reportId, localFastFillAccepted, enableFastFillManagement]);

  const handleStatusChange = useCallback(async (status: TestStatus) => {
    methods.setValue("status", status);

    if (status === 'published' || status === 'qa') {
      methods.handleSubmit(handleSave)();
    } else {
      const data = methods.getValues();
      await handleSave(data);
    }

  }, [methods, handleSave]);



  // useImperativeHandle(ref, () => ({
  //   resetForm: resetForm
  // }), [resetForm]);

  useImperativeHandle(ref, () => methods);

  useEffect(() => {
    onDirtyChange?.(isFormDirty);
  }, [isFormDirty, onDirtyChange]);

  useEffect(() => {

    if (dirtyFlagRef.current) {
      // Don't reset the form if it's dirty; Reset can be triggered by the test or test suite updating
      // Due to new options added to the underlying data
      // return;
    }

    setFormIsLoading(true);
    initialErrorState.current = {};
    hasInitializedErrorState.current = false;
    pendingReferenceImageElections.current = {};

    console.debug('Form reset effect', { testItem, isDirty: dirtyFlagRef.current, isFormReady });


    let formInitialValues: Partial<TestFormValues> = {};

    if (testItem) {
      // If editing an existing test, use its data
      formInitialValues = convertInspectionToFormValues(testItem);
    } else if (defaultInspectionId) {
      // If creating a new test AND the prop is provided, set the default
      formInitialValues = { assessmentId: defaultInspectionId };
    }

    console.debug('Initializing form with:', formInitialValues);
    resetFormRef.current(formInitialValues);

    setIsExistingTest(!!testItem?.assessmentTestId);

    console.debug('Form reset', { testItem, initialData });
    methods.trigger();
    fastFillChangedRef.current.clear();

    setTimeout(() => {
      setFormIsLoading(false);
    }, 50);

  }, [
    testItem?.assessmentTestId,
    testItem?.testSpecificationId,
    testItem?.formId,
    testItem?.assetId,
    testItem?.assessmentId,
    methods
  ]);

  const formState = useFormState({
    control: methods.control
  });



  useEffect(() => {
    const { errors, isValid, dirtyFields, touchedFields, isDirty } = formState;
    const values = methods.getValues();

    const requiredFieldsFilled = !!values.assessmentId &&
      !!values.test_specification_id &&
      !!values.assetId;

    const issues = findHookFormFieldRegistrationIssues(formState.defaultValues, values);

    if (!isFormLoading && isFormReady && Object.keys(issues)?.length > 0) {
      console.debug('Registration issues', issues);
    }

    if (!isFormLoading && isFormReady) {
      console.debug('Are reqd fields filled', {
        isValid, requiredFieldsFilled, errors,
        isDirty, dirtyFields,
        values: values,
        defaultValues: formState.defaultValues
      });
    }

    setIsFormValid(isValid);
    setIsFormDirty(isDirty);
    setAreRequiredFieldsFilled(requiredFieldsFilled);

    let cancelled = false;
    isSubmissionReadyForStatus(values, testSuites, 'published').then((ready) => {
      if (!cancelled) setIsReadyForNonDraftStatus(ready);
    });

    return () => {
      cancelled = true;
    };


  }, [formState, methods, testSuites]);

  // Track fast fill acceptance changes as dirty state
  useEffect(() => {
    if (!enableFastFillManagement) return;

    const initialFastFillAccepted = (testItem as any)?.switches?.fast_fill_accepted ?? false;
    const hasFastFillChanged = localFastFillAccepted !== initialFastFillAccepted;

    // If fast fill changed but form isn't marked dirty, mark it dirty
    if (hasFastFillChanged && !isFormDirty) {
      setIsFormDirty(true);
    }

    // If fast fill reverted to original AND form is otherwise clean, mark it clean
    if (!hasFastFillChanged && isFormDirty && !formState.isDirty) {
      setIsFormDirty(false);
    }
  }, [localFastFillAccepted, testItem?.assessmentTestId, enableFastFillManagement, isFormDirty, formState.isDirty]);

  useEffect(() => {
    if (!hasInitializedErrorState.current && isFormReady) {
      const values = methods.getValues();
      Promise.resolve(resolver(values, undefined, {} as any)).then(result => {
        initialErrorState.current = result.errors;
        hasInitializedErrorState.current = true;
      });
    }
  }, [resolver, isFormReady, methods]);

  // Throttle error updates to avoid excessive calls
  useEffect(() => {
    if (!onErrors) return;

    const timeoutId = setTimeout(() => {
      if (JSON.stringify(formState?.errors) !== JSON.stringify(initialErrorState.current)) {
        onErrors?.(formState?.errors);
      }
    }, 500); // 500ms throttle

    return () => clearTimeout(timeoutId);
  }, [formState?.errors, onErrors]);



  const [addTestSpecificationFastFill, { isLoading: isAddingFastFill }] = useAddTestSpecificationFastFillMutation();

  const handleFastFillAdd = useCallback(async (type: 'observations' | 'recommendations', text: string) => {
    if (!selectedTestSuite.suite.allow_add_fast_fills) {
      toast.error('Fast fill is not allowed for this test suite');
      return;
    }
    try {
      await addTestSpecificationFastFill({
        testSpecificationId: selectedTestSuite.specification.id,
        data: {
          type,
          description: text
        }
      }).unwrap();

      toast.success('Fast fill added');
    } catch (err) {
      console.error('Error adding fast fill', err);
      toast.error('Error adding fast fill');
    }

  }, [selectedTestSuite, addTestSpecificationFastFill]);

  const getFieldVisibility = useCallback((fieldName: string) => {
    if (!selectedTestSuite || !selectedTestSuite.suite.settings) return true;
    const defaultSettings = getDefaultFieldSettings(fieldName, selectedTestSuite?.suite?.type);
    const field = selectedTestSuite.suite.settings.find(s => s.field_name === fieldName);
    return field ? field.active : defaultSettings.active;
  }, [selectedTestSuite]);

  const getFieldRequirement = useCallback((fieldName: string) => {
    if (['assessmentId', 'assetId', 'test_specification_id'].includes(fieldName)) return true;
    if (!selectedTestSuite || !selectedTestSuite.suite) return false;

    if (isCustomField(fieldName)) {
      const customFieldId = getCustomFieldId(fieldName);

      const field = selectedTestSuite.suite.custom_fields?.find(s => s.field_id === customFieldId);
      return field ? field.required ?? false : false;
    } else {
      const defaultSettings = getDefaultFieldSettings(fieldName, selectedTestSuite?.suite?.type);
      const field = selectedTestSuite.suite.settings?.find(s => s.field_name === fieldName);
      return field ? field.required ?? false : defaultSettings.required;
    }
  }, [selectedTestSuite]);

  const getFieldDefinition = useCallback((fieldName: string): FieldDefinition | undefined => {
    if (!selectedTestSuite || !selectedTestSuite.suite) return undefined;

    if (isCustomField(fieldName)) {
      const customFieldId = getCustomFieldId(fieldName);
      const fieldDef = selectedTestSuite.suite.custom_fields?.find(s => s.field_id === customFieldId);
      return fieldDef as FieldDefinition | undefined;
    } else {
      const fieldDef = selectedTestSuite.suite.settings?.find(s => s.field_name === fieldName);
      return fieldDef ? { ...fieldDef, type: fieldDef.type ?? 'unknown' } as FieldDefinition : undefined;
    }
  }, [selectedTestSuite]);

  const [registeredKeyHandler, setRegisteredKeyHandler] = useState<((e: React.KeyboardEvent<HTMLFormElement>) => void) | null>(null);

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    } else {
      if (registeredKeyHandler) {
        registeredKeyHandler(e as React.KeyboardEvent<HTMLFormElement>);
      }
    }
  }, [registeredKeyHandler]);

  const handleTriggerValidation = useCallback(() => {
    methods.trigger();
  }, [methods.trigger]);

  const handleMediaChange = useCallback((updatedMedia: MediaInfo[]) => {
    const currentFiles = methods.getValues('images').filter(img => img instanceof File);
    methods.setValue('images', [...updatedMedia, ...currentFiles], {
      shouldValidate: true,
      shouldDirty: true
    });
  }, [methods]);

  const {
    handleCreateBuilding,
    handleCreateLevel,
    handleCreateArea
  } = useSpaceCreation({ asset_id })

  const [createElement, { isLoading: isAddingElement, isSuccess: isElementCreated, isError: isCreationError }] = useCreateClassificationItemMutation();

  const handleCreateElement = useCallback(async (type: 'item' | 'tree', node: ClassificationItem | ClassificationTree, element: string) => {
    console.debug('Creating element', { node, type, element });
    const result = await createElement({
      name: element,
      parent_id: type === 'item' ? (node as ClassificationItem).id : null,
      classification_tree_id: type === 'item' ? (node as ClassificationItem).classification_tree_id : (node as ClassificationTree).id
    }).unwrap();

    toast.success('Element created');

    return result;

  }, [createElement]);

  const fastFillChangedRef = useRef<Set<string>>(new Set());

  // ---> Add state for fast fill error fields <--- 
  const [fastFillErrorFields, setFastFillErrorFields] = useState(new Set<string>());

  // Reset local fast fill accepted when testItem changes (only if management is enabled)
  useEffect(() => {
    if (enableFastFillManagement) {
      setLocalFastFillAccepted((testItem as any)?.switches?.fast_fill_accepted ?? false);
    }
  }, [testItem?.assessmentTestId, (testItem as any)?.switches?.fast_fill_accepted, enableFastFillManagement]);

  // Monitor fastFillErrorFields - when errors are registered, check if any were user-changed fields
  useEffect(() => {
    if (!enableFastFillManagement) return;

    // Only auto-turn-off if acceptance is currently on
    // We check localFastFillAccepted here but don't include it in deps
    // This allows manual re-enabling without immediately disabling again
    if (!localFastFillAccepted) return;

    // When a new error is registered, check if it's for a field the user changed
    if (fastFillErrorFields.size > 0 && fastFillChangedRef.current.size > 0) {
      // Check if any error fields were changed by the user
      const changedFieldsWithErrors = Array.from(fastFillErrorFields).some(
        fieldId => fastFillChangedRef.current.has(fieldId)
      );

      if (changedFieldsWithErrors) {
        // Turn off fast fill acceptance locally (don't call API)
        console.debug('Fast fill errors detected on user-changed fields, turning off acceptance locally');
        setLocalFastFillAccepted(false);
      }
    }
    // Note: localFastFillAccepted is NOT in dependencies to avoid feedback loop
    // when user manually toggles acceptance back on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fastFillErrorFields, enableFastFillManagement]);

  const handleFastFillChanged = useCallback((field_id: string) => {
    fastFillChangedRef.current.add(field_id);

    // Immediately check if this field has errors and turn off acceptance if needed
    if (enableFastFillManagement && localFastFillAccepted && fastFillErrorFields.has(field_id)) {
      console.debug('Fast fill error detected on changed field, turning off acceptance locally', field_id);
      setLocalFastFillAccepted(false);
    }
  }, [enableFastFillManagement, localFastFillAccepted, fastFillErrorFields]);

  // Callback to allow manual toggling
  const handleFastFillAcceptedChange = useCallback((value: boolean) => {
    setLocalFastFillAccepted(value);
  }, []);

  // ---> Add callbacks for registering/unregistering fast fill errors <--- 
  const registerFastFillError = useCallback((fieldId: string) => {
    setFastFillErrorFields(prev => new Set(prev).add(fieldId));
  }, []);

  const unregisterFastFillError = useCallback((fieldId: string) => {
    setFastFillErrorFields(prev => {
      const next = new Set(prev);
      next.delete(fieldId);
      return next;
    });
  }, []);

  useEffect(() => {
    return () => {
      console.debug('form: unmounted');
      //clear tes item and reset state
      setTestState({
        selectedTestSpecificationId: null,
        isTestSelected: false
      });

      methods.reset();

      setIsFormReady(false);
      setIsFormDirty(false);
      setIsFormValid(false);
    }
  }, []);

  const saveForm = useCallback(async () => {
    const data = methods.getValues();
    return await handleSave(data);
  }, [methods, handleSave]);

  const contextValue = useMemo(() => ({
    formId: formId.current,
    isExistingTest,
    isFormDirty,
    isFormValid,
    isFormReady,
    isRequiredFieldsFilled: areRequiredFieldsFilled,
    isReadyForNonDraftStatus,
    selectedTestSuite,
    viewportRef: scrollAreaRef,
    getFieldVisibility,
    getFieldRequirement,
    getFieldDefinition,
    onStatusChange: handleStatusChange,
    onFastFillChanged: handleFastFillChanged,
    fastFillErrorFields,
    registerFastFillError,
    unregisterFastFillError,
    localFastFillAccepted,
    setLocalFastFillAccepted: handleFastFillAcceptedChange,
    initialErrorState: initialErrorState.current,
    isSaving,
    formRef: formRef?.current,
    saveForm
  }), [
    formId.current,
    isExistingTest,
    isFormDirty,
    isFormValid,
    isFormReady,
    areRequiredFieldsFilled,
    isReadyForNonDraftStatus,
    selectedTestSuite,
    getFieldVisibility,
    getFieldRequirement,
    getFieldDefinition,
    handleStatusChange,
    handleFastFillChanged,
    fastFillErrorFields,
    registerFastFillError,
    unregisterFastFillError,
    localFastFillAccepted,
    handleFastFillAcceptedChange,
    initialErrorState?.current,
    isSaving,
    formRef?.current,
    saveForm
  ]);


  if (isSpacesLoading || elementTreeIsLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <InspectionFormSkeleton />
      </motion.div>
    )
  }


  if (!inspections || !testSuites || !assets) {
    return null;
  }

  return (
    (
      <TestFormContext.Provider value={contextValue}>

        <FormWrapperContext.Provider value={{ registerKeyDownHandler: setRegisteredKeyHandler }}>
          <FormProvider {...methods}>
            <Form {...methods}>

              <form ref={formRef}
                className={cn("flex flex-col h-full", {
                  "opacity-80": isSaving,
                  "cursor-not-allowed": isSaving
                })}
                onKeyDown={handleFormKeyDown}
                onSubmit={methods.handleSubmit(onSubmit)}>
                <HeaderComponent
                  testItem={testItem}
                  enableRightContent={enableRightContent}
                  saveStatusComponent={<StatusIndicator />}
                // errorNavigationComponent={<ErrorNavigation />}
                />

                <FormWrapperComponent
                  formId={testItem?.formId ?? 'test-edit-form'}
                  scrollAreaRef={scrollAreaRef}
                >
                  {/* <ScrollArea className="h-full pb-10" ref={scrollAreaRef}> */}

                  <div className="px-6 pt-4 pb-2">

                    <FieldContainerComponent
                      field_id="assessmentId"
                      showRightComponent={enableRightContent}
                    >
                      <InspectionSelector
                        name="assessmentId"
                        label="Inspection"
                        required={true}
                        inspections={groupedInspections}
                        editable={!isExistingTest && !defaultInspectionId}
                      />
                    </FieldContainerComponent>

                    <FieldContainerComponent
                      field_id="assessmentId"
                      showRightComponent={enableRightContent}
                    >
                      <AssetDisplay
                        name="assetId"
                        label="Asset"
                        required={true}
                        inspections={groupedInspections} />
                    </FieldContainerComponent>

                    <FieldContainerComponent
                      field_id="test_specification_id"
                      showRightComponent={enableRightContent}
                    >
                      <TestSpecificationSelector
                        name="test_specification_id"
                        label="Test"
                        testSuites={filteredTestSuites}
                        onSelect={handleTestSelect}
                        disabled={!selectedAssessment}
                        labelComponent={<FormLabelWithIndicator label="Test" isValid={!!methods.watch("test_specification_id")} required={true} />}
                      />
                    </FieldContainerComponent>

                    {guidanceEnabled && selectedTestSuite?.specification.test_guidance && (
                      <FieldContainerComponent
                        field_id="test-guidance"
                        showRightComponent={enableRightContent}
                      >
                        <FormLabelWithIndicator label="Test Guidance" isValid required={false} />
                        <GuidanceView
                          markdown={selectedTestSuite.specification.test_guidance}
                          referenceImages={specReferenceImages}
                          onOpenReferenceImage={handleOpenReferenceImage}
                        />
                      </FieldContainerComponent>
                    )}

                    {hasResolvedReferenceImages && (
                      <FieldContainerComponent
                        field_id="reference-images"
                        showRightComponent={enableRightContent}
                      >
                        <FormLabelWithIndicator label="Reference Images" isValid required={false} />
                        <TestItemReferenceImages
                          testItemId={testItem.assessmentTestId}
                          images={resolvedReferenceImages}
                          editable
                        />
                      </FieldContainerComponent>
                    )}

                    {!hasResolvedReferenceImages && guidanceEnabled && fallbackReferenceImages.length > 0 && (
                      <FieldContainerComponent
                        field_id="reference-images"
                        showRightComponent={enableRightContent}
                      >
                        <FormLabelWithIndicator label="Reference Images" isValid required={false} />
                        <TestItemReferenceImages
                          testItemId={testItem?.assessmentTestId ?? 0}
                          images={fallbackReferenceImages}
                          editable
                          onElect={testItem?.assessmentTestId ? undefined : handleReferenceImageElect}
                        />
                      </FieldContainerComponent>
                    )}

                    {isLoadingTestSuiteData && (
                      <div className="flex justify-center items-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    )}

                    <AnimatePresence>
                      {selectedTestSuite && areRequiredFieldsFilled && !isLoadingTestSuiteData && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: .5 }}
                          className=""
                        >
                          {/* Render form fields based on visibility and requirement */}

                          {getFieldVisibility('location') && (
                            <FieldContainerComponent
                              field_id="location"
                              showRightComponent={enableRightContent}
                            >
                              <FormField
                                control={methods.control}
                                name="building_id"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator
                                      label="Location"
                                      isValid={!!field.value}
                                      required={getFieldRequirement('location')}
                                    />
                                    <FormControl>
                                      <SpaceSelector
                                        spaces={organizedSpaces as any}
                                        disabled={!!!asset_id}
                                        isLoading={false}
                                        defaultValues={{
                                          building_id: field.value,
                                          level_id: methods.watch('level_id'),
                                          area_id: methods.watch('area_id')
                                        }}
                                        onSelect={handleTriggerValidation}
                                        onCreateBuilding={handleCreateBuilding}
                                        onCreateLevel={handleCreateLevel}
                                        onCreateArea={handleCreateArea}
                                        canCreate={true}
                                      />

                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('element') && (
                            <FieldContainerComponent
                              field_id="element"
                              showRightComponent={enableRightContent}
                            >
                              <FormField
                                control={methods.control}
                                name="element"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator label="Element" isValid={!!field.value} required={getFieldRequirement('element')} />
                                    <FormControl>
                                      <ElementSelector
                                        name="element"
                                        elements={elementGroups}
                                        onSelect={(value) => field.onChange(value)}
                                        isLoading={false}
                                        onCreateElement={handleCreateElement}
                                      />

                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('element_name') && (
                            <FieldContainerComponent
                              field_id="element_name"
                              showRightComponent={enableRightContent}
                            >
                              <FormField
                                control={methods.control}
                                name="element_name"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator label="Element Name" isValid={!!field.value} required={getFieldRequirement('element_name')} />
                                    <FormControl>
                                      <Input {...field}
                                        placeholder="Enter Element Name"
                                        spellCheck={true}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('element_id') && (
                            <FieldContainerComponent
                              field_id="element_id"
                              showRightComponent={enableRightContent}
                            >
                              <FormField
                                control={methods.control}
                                name="element_id"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator label="Element ID" isValid={!!field.value} required={getFieldRequirement('element_id')} />
                                    <FormControl>
                                      <Input {...field}
                                        placeholder="Enter Element ID"
                                        spellCheck={true}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('rating') && (
                            <FieldContainerComponent
                              field_id="rating"
                              showRightComponent={enableRightContent}
                              key={`rating-${testItem?.assessmentTestId ?? formId.current}`}

                            >
                              <FormField
                                control={methods.control}
                                name="result"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator
                                      label="Result"
                                      isValid={!!field.value}
                                      required={getFieldRequirement('rating')}
                                    />
                                    <FormControl>
                                      <RaytdResultPicker
                                        key={`rating-picker-${testItem?.assessmentTestId ?? formId.current}`}
                                        name="result"
                                        ref={field.ref}
                                        onChange={(value) => field.onChange(value)}
                                        onBlur={field.onBlur}
                                        value={field.value}
                                        testSuite={selectedTestSuite.suite}
                                        allowNotApplicable={!getFieldRequirement('rating')}
                                        showClearButton={!getFieldRequirement('rating')}
                                        allowDeselection={!getFieldRequirement('rating')}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('observations') && (
                            <FieldContainerComponent
                              field_id="observations"
                              showRightComponent={enableRightContent}
                              isFastFill={selectedTestSuite?.suite.type === 'compliance' && selectedTestSuite?.specification?.observations?.length > 0}
                            >
                              <FormField
                                control={methods.control}
                                name="observations"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator label="Observations" isValid={!!field.value} required={getFieldRequirement('observations')} />
                                    <FormControl>

                                      <FastFillText
                                        control={methods.control}
                                        name="observations"
                                        enableHighlighting={enableFastFillHighlighting}
                                        fastFills={selectedTestSuite?.specification?.observations ?? []}
                                        enableFastFill={selectedTestSuite?.suite.type === 'compliance'}
                                        allowAdd={selectedTestSuite?.suite.allow_add_fast_fills}
                                        onCreateOption={(value) => handleFastFillAdd('observations', value)}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('recommendations') && (
                            <FieldContainerComponent
                              field_id="recommendations"
                              showRightComponent={enableRightContent}
                              isFastFill={selectedTestSuite?.suite.type === 'compliance' && selectedTestSuite?.specification?.recommendations?.length > 0}
                            >
                              <FormField
                                control={methods.control}
                                name="recommendations"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator label="Recommendations" isValid={!!field.value} required={getFieldRequirement('recommendations')} />
                                    <FormControl>
                                      <FastFillText
                                        control={methods.control}
                                        enableHighlighting={enableFastFillHighlighting}
                                        name="recommendations"
                                        fastFills={selectedTestSuite?.specification?.recommendations ?? []}
                                        enableFastFill={selectedTestSuite?.suite.type === 'compliance'}
                                        allowAdd={selectedTestSuite?.suite.allow_add_fast_fills}
                                        onCreateOption={(value) => handleFastFillAdd('recommendations', value)}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )} />
                            </FieldContainerComponent>
                          )}

                          {getFieldVisibility('photographs') && (
                            <FieldContainerComponent
                              field_id="photographs"
                              showRightComponent={enableRightContent}
                            >
                              <FormField
                                control={methods.control}
                                name="images"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabelWithIndicator
                                      label="Photographs"
                                      isValid={(field.value?.filter((img: { deleted?: boolean }) => !img.deleted)?.length ?? 0) > 0}
                                      required={getFieldRequirement('photographs')}
                                    />
                                    <FormControl>

                                      <FileUploader
                                        ref={field.ref}
                                        formId={formId.current}
                                        value={field.value.filter((img): img is MediaInfo => 'id' in img)}
                                        // onChange={field.onChange}
                                        onChange={handleMediaChange}
                                        maxFiles={50}
                                        maxSize={20000000}
                                        acceptedTypes={["image/jpeg", "image/jpg", "image/png"]}
                                        disabled={methods.formState.isSubmitting}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </FieldContainerComponent>
                          )}


                          {testState.selectedTestSpecificationId && (
                            <CustomFieldsContainer
                              name="custom"
                              testSuite={selectedTestSuite.suite}
                              enableRightComponent={enableRightContent}
                              onCustomFieldOptionAdded={onCustomFieldOptionAdded}
                              FieldContainerComponent={FieldContainerComponent}
                              enableFastFillHighlighting={enableFastFillHighlighting}
                            />
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  {/* </ScrollArea> */}
                </FormWrapperComponent>

              </form>
            </Form>
          </FormProvider>
        </FormWrapperContext.Provider>

        <TestFormDebugger
          methods={methods}
          resetForm={resetForm}
          formId={formId.current}
          selectedTestSuite={selectedTestSuite}
        />
      </TestFormContext.Provider>
    )
  );
});

export { TestEditForm }
